import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { RealtimePostgresChangesPayload, RealtimeChannel } from '@supabase/supabase-js';

export interface UserMessage {
  id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  read: boolean;
  created_at: string;
  sender_name?: string;
  recipient_name?: string;
}

export interface UserConnection {
  id: string;
  user_id: string;
  connected_user_id: string;
  status: 'pending' | 'accepted' | 'declined';
  created_at: string;
  updated_at: string;
  connected_user_name?: string;
  connected_user_avatar?: string;
}

interface MessagePayload {
  id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  read: boolean;
  created_at: string;
}

interface ConnectionPayload {
  id: string;
  user_id: string;
  connected_user_id: string;
  status: 'pending' | 'accepted' | 'declined';
  created_at: string;
  updated_at: string;
}

export function useUserMessages() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<UserMessage[]>([]);
  const [connections, setConnections] = useState<UserConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeConversation, setActiveConversation] = useState<string | null>(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [pendingConnectionCount, setPendingConnectionCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  const profileCache = useRef(new Map<string, { name: string; avatar?: string }>());

  const fetchProfileData = useCallback(async (userId: string) => {
    if (profileCache.current.has(userId)) {
      return profileCache.current.get(userId)!;
    }
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('full_name, username, avatar_url')
      .eq('id', userId)
      .single();
    if (error) {
      console.error('Error fetching profile:', error);
      const fallback = { name: 'Unknown User', avatar: undefined };
      profileCache.current.set(userId, fallback);
      return fallback;
    }
    const result = {
      name: profile.full_name || profile.username || 'Unknown User',
      avatar: profile.avatar_url || undefined
    };
    profileCache.current.set(userId, result);
    return result;
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_messages')
        .select('*')
        .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order('created_at', { ascending: true });
      if (error) throw error;
      const userIds = new Set<string>();
      data.forEach(m => {
        userIds.add(m.sender_id);
        userIds.add(m.recipient_id);
      });
      await Promise.all(Array.from(userIds).map(fetchProfileData));
      const withNames = data.map(m => ({
        ...m,
        sender_name: profileCache.current.get(m.sender_id)?.name,
        recipient_name: profileCache.current.get(m.recipient_id)?.name
      }));
      setMessages(withNames);
    } catch (err) {
      console.error('[useUserMessages] Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  }, [user, fetchProfileData]);

  const fetchConnections = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('user_connections')
        .select('*')
        .or(`user_id.eq.${user.id},connected_user_id.eq.${user.id}`)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      const userIds = new Set<string>();
      data.forEach(c => {
        userIds.add(c.user_id);
        userIds.add(c.connected_user_id);
      });
      await Promise.all(Array.from(userIds).map(fetchProfileData));
      const withNames = data.map(conn => {
        const other = conn.user_id === user.id ? conn.connected_user_id : conn.user_id;
        return {
          ...conn,
          connected_user_name: profileCache.current.get(other)?.name,
          connected_user_avatar: profileCache.current.get(other)?.avatar
        };
      });
      setConnections(withNames);
    } catch (err) {
      console.error('Error fetching connections:', err);
    }
  }, [user, fetchProfileData]);

  // Combined effect for data fetching and real-time subscriptions
  useEffect(() => {
    if (!user) {
      setMessages([]);
      setConnections([]);
      setOnlineUsers(new Set());
      return;
    }

    fetchMessages();
    fetchConnections();

    const handleMessageChange = async (payload: RealtimePostgresChangesPayload<any>) => {
      if (payload.eventType === 'INSERT') {
        const newMessage = payload.new as UserMessage;
        if (newMessage.sender_id === user.id || newMessage.recipient_id === user.id) {
          const senderProfile = await fetchProfileData(newMessage.sender_id);
          const recipientProfile = await fetchProfileData(newMessage.recipient_id);
          const messageWithNames: UserMessage = { ...newMessage, sender_name: senderProfile.name, recipient_name: recipientProfile.name };
          setMessages(prev => prev.some(m => m.id === messageWithNames.id) ? prev : [...prev, messageWithNames]);
        }
      } else {
        fetchMessages();
      }
    };

    const handleConnectionChange = (payload: RealtimePostgresChangesPayload<any>) => {
      fetchConnections();
    };

    const randomId = Math.random().toString(36).substring(2, 9);
    const messagesChannel = supabase.channel(`rt-messages-${user.id}-${randomId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_messages' }, handleMessageChange)
      .subscribe();

    const connectionsChannel = supabase.channel(`rt-connections-${user.id}-${randomId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_connections' }, handleConnectionChange)
      .subscribe();

    // Single shared channel name so all signed-in clients can see each other.
    // Supabase's channel registry returns the SAME instance for a given
    // topic, and calling .on('presence', ...) on an already-subscribed
    // channel throws. Guard against that for re-mounts (React StrictMode,
    // route changes, multiple components using this hook).
    const presenceTopic = 'realtime:global-presence';
    const existingPresence = supabase
      .getChannels()
      .find((c) => c.topic === presenceTopic);
    const presenceChannel =
      existingPresence ??
      supabase.channel('global-presence', {
        config: { presence: { key: user.id } },
      });
    const isPresenceOwner = !existingPresence;

    const syncOnlineUsers = () => {
      const userIds = new Set<string>();
      const state = presenceChannel.presenceState();
      for (const id in state) userIds.add(id);
      setOnlineUsers(userIds);
    };

    if (isPresenceOwner) {
      presenceChannel
        .on('presence', { event: 'sync' }, syncOnlineUsers)
        .subscribe(async (status) => {
          if (status === 'SUBSCRIBED') {
            await presenceChannel.track({
              user_id: user.id,
              online_at: new Date().toISOString(),
            });
          }
        });
    } else {
      // Reusing a channel a previous mount set up — seed our local state
      // from the current presence map and re-track ourselves.
      syncOnlineUsers();
      presenceChannel
        .track({ user_id: user.id, online_at: new Date().toISOString() })
        .catch(() => undefined);
    }

    return () => {
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(connectionsChannel);
      if (isPresenceOwner) supabase.removeChannel(presenceChannel);
    };
  }, [user, fetchMessages, fetchConnections, fetchProfileData]);

  const sendMessage = useCallback(async (recipientId: string, content: string) => {
    if (!user || !recipientId || !content.trim()) return null;
    try {
      const { data: newMessage, error } = await supabase
        .from('user_messages')
        .insert({ sender_id: user.id, recipient_id: recipientId, content, read: false })
        .select()
        .single();
      if (error) throw error;
      // The UI will now be updated by the realtime subscription broadcast.
      return newMessage;
    } catch (err) {
      console.error('Error sending message:', err);
      toast({ title: "Error", description: "Failed to send message.", variant: "destructive" });
      return null;
    }
  }, [user, toast]);

  const markAsRead = useCallback(async (messageId: string) => {
    try {
      const { error } = await supabase.from('user_messages').update({ read: true }).eq('id', messageId);
      if (error) throw error;
    } catch (err) {
      console.error('Error marking message as read:', err);
    }
  }, []);
  
  const requestConnection = useCallback(async (targetUserId: string) => {
    if (!user || !targetUserId) return null;
    try {
      const { data: existing } = await supabase
        .from('user_connections')
        .select('id, status')
        .or(`and(user_id.eq.${user.id},connected_user_id.eq.${targetUserId}),and(user_id.eq.${targetUserId},connected_user_id.eq.${user.id})`)
        .maybeSingle();
      if (existing && (existing.status === 'pending' || existing.status === 'accepted')) {
        toast({ title: "Already connected or pending", description: "You already have a connection or pending request with this user." });
        return null;
      }
      const { data, error } = await supabase
        .from('user_connections')
        .insert({ user_id: user.id, connected_user_id: targetUserId, status: 'pending' })
        .select().single();
      if (error) throw error;
      fetchConnections();
      return data;
    } catch (err) {
      console.error('Error creating connection:', err);
      toast({ title: "Error", description: "Failed to send connection request.", variant: "destructive" });
      return null;
    }
  }, [user, fetchConnections, toast]);

  const updateConnectionStatus = useCallback(async (connectionId: string, newStatus: 'accepted' | 'declined') => {
    try {
      const { error } = await supabase.from('user_connections').update({ status: newStatus }).eq('id', connectionId);
      if (error) throw error;
      fetchConnections();
      toast({ title: newStatus === 'accepted' ? "Connection accepted" : "Connection declined" });
    } catch (err) {
      console.error('Error updating connection status:', err);
      toast({ title: "Error", description: "Failed to update connection status.", variant: "destructive" });
    }
  }, [fetchConnections, toast]);

  // Notification counts
  useEffect(() => {
    if (!user) {
      setUnreadMessageCount(0);
      setPendingConnectionCount(0);
      return;
    }
    const unread = messages.filter(m => m.recipient_id === user.id && !m.read).length;
    setUnreadMessageCount(unread);
    const pending = connections.filter(c => c.status === 'pending' && c.connected_user_id === user.id).length;
    setPendingConnectionCount(pending);
  }, [messages, connections, user]);

  return {
    messages,
    connections,
    loading,
    activeConversation,
    setActiveConversation,
    sendMessage,
    markAsRead,
    requestConnection,
    updateConnectionStatus,
    unreadMessageCount,
    pendingConnectionCount,
    onlineUsers,
  };
}